import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  NewCatalogAppliedPropertyFilter,
  CatalogIdentityBrandConflict,
  CatalogIdentityBrandIssue,
  CatalogIdentityProductDuplicateGroup,
  NewCatalogCreateProductFromSupplierInfoPayload,
  NewCatalogCreateProductFromSupplierInfoResponse,
  NewCatalogPartApplicability,
  NewCatalogPartInfo,
  NewCatalogProductImagesResponse,
  NewCatalogProductStock,
  NewCatalogProductSearchResponse,
  NewCatalogSupplierMissingBulkCreateResult,
  NewCatalogSupplierMissingCreatePayload,
  NewCatalogSupplierMissingActionResult,
  NewCatalogSupplierMissingResponse,
  ProductRepricingStartResult
} from 'src/typings';

export type NewCatalogSortActive = 'id' | 'description' | 'brand' | 'article' | 'price' | 'stock';
export type NewCatalogSortDirection = 'asc' | 'desc';

export interface NewCatalogNextFifoLot {
  lotId: number;
  warehouseId: number;
  warehouseName: string;
  receiptDate: string;
  unitCost: number;
  remainingQty: number;
}

export interface NewCatalogStockLotsResponse {
  items: NewCatalogNextFifoLot[];
}

export interface NewCatalogNextFifoLotsResponse {
  items: NewCatalogNextFifoLot[];
  averageUnitCost: number | null;
}

export type NewCatalogSupplierMissingSortActive =
  | 'brand'
  | 'article'
  | 'name'
  | 'provider'
  | 'price'
  | 'stock'
  | 'updatedAt';

@Injectable({
  providedIn: 'root'
})
export class NewCatalogService {
  constructor(private http: HttpClient) {}

  searchProducts(
    query: string,
    limit = 50,
    offset = 0,
    brandIds: number[] = [],
    propertyFilters: NewCatalogAppliedPropertyFilter[] = [],
    sortActive?: NewCatalogSortActive,
    sortDirection?: NewCatalogSortDirection,
    includeFacets = true,
    folderId?: number | null,
    searchMode: 'v1' | 'v2' = 'v1'
  ): Observable<NewCatalogProductSearchResponse> {
    let params = new HttpParams().set('query', query).set('limit', limit).set('offset', offset);
    if (searchMode === 'v2') {
      params = params.set('search_mode', 'v2');
    }
    if (folderId) {
      params = params.set('folder_id', folderId);
    }
    brandIds.forEach((brandId) => {
      params = params.append('brand_ids', brandId);
    });
    propertyFilters.forEach((filter) => {
      if (filter.mode === 'values') {
        filter.values.forEach((value) => {
          params = params.append('properties', `${filter.property_id}:${value}`);
        });
        return;
      }

      if (filter.mode === 'range') {
        const min = filter.min === null || filter.min === undefined ? '' : filter.min;
        const max = filter.max === null || filter.max === undefined ? '' : filter.max;
        params = params.append('property_ranges', `${filter.property_id}:${min}:${max}`);
        return;
      }

      params = params.append('property_contains', `${filter.property_id}:${filter.value}`);
    });
    if (sortActive && sortDirection) {
      params = params.set('sort_active', sortActive).set('sort_direction', sortDirection);
    }
    if (!includeFacets) {
      params = params.set('include_facets', 'false');
    }

    return this.http.get<NewCatalogProductSearchResponse>(
      `${environment.BACKEND}/new-catalog/products/search`,
      {
        params
      }
    );
  }

  getPartInfo(productId: number): Observable<NewCatalogPartInfo> {
    return this.http.get<NewCatalogPartInfo>(
      `${environment.BACKEND}/new-catalog/products/${productId}/part-info`
    );
  }

  getProductImages(productIds: number[]): Observable<NewCatalogProductImagesResponse> {
    let params = new HttpParams();
    productIds.forEach((productId) => {
      params = params.append('product_ids', productId);
    });

    return this.http.get<NewCatalogProductImagesResponse>(
      `${environment.BACKEND}/new-catalog/products/images`,
      { params }
    );
  }

  getNextFifoLots(productId: number): Observable<NewCatalogNextFifoLotsResponse> {
    return this.http.get<NewCatalogNextFifoLotsResponse>(
      `${environment.BACKEND}/new-catalog/products/${productId}/next-fifo-lots`
    );
  }

  getStockLots(productId: number): Observable<NewCatalogStockLotsResponse> {
    return this.http.get<NewCatalogStockLotsResponse>(
      `${environment.BACKEND}/new-catalog/products/${productId}/stock-lots`
    );
  }

  updateMinimumStock(payload: {
    product_id: number;
    warehouse_id: number;
    minimum_stock: number;
  }): Observable<NewCatalogProductStock> {
    return this.http.put<NewCatalogProductStock>(
      `${environment.BACKEND}/warehouse/minimum-stock`,
      payload
    );
  }

  getPartApplicability(productId: number): Observable<NewCatalogPartApplicability> {
    return this.http.get<NewCatalogPartApplicability>(
      `${environment.BACKEND}/new-catalog/products/${productId}/part-applicability`
    );
  }

  getCatalogIdentityProductDuplicates(
    limit = 200
  ): Observable<CatalogIdentityProductDuplicateGroup[]> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<CatalogIdentityProductDuplicateGroup[]>(
      `${environment.BACKEND}/catalog-identity-review/product-duplicates`,
      {
        params
      }
    );
  }

  getCatalogIdentityBrandIssues(limit = 200, offset = 0): Observable<CatalogIdentityBrandIssue[]> {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http.get<CatalogIdentityBrandIssue[]>(
      `${environment.BACKEND}/catalog-identity-review/brand-issues`,
      {
        params
      }
    );
  }

  getCatalogIdentityBrandConflicts(limit = 200): Observable<CatalogIdentityBrandConflict[]> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<CatalogIdentityBrandConflict[]>(
      `${environment.BACKEND}/catalog-identity-review/brand-conflicts`,
      {
        params
      }
    );
  }

  searchSupplierMissingProducts(
    query: string,
    limit = 50,
    offset = 0,
    sortActive: NewCatalogSupplierMissingSortActive = 'brand',
    sortDirection: NewCatalogSortDirection = 'asc',
    providerIds: number[] = []
  ): Observable<NewCatalogSupplierMissingResponse> {
    let params = new HttpParams()
      .set('query', query)
      .set('limit', limit)
      .set('offset', offset)
      .set('sort_active', sortActive)
      .set('sort_direction', sortDirection);
    providerIds.forEach((providerId) => {
      params = params.append('provider_ids', providerId);
    });

    return this.http.get<NewCatalogSupplierMissingResponse>(
      `${environment.BACKEND}/new-catalog/supplier-missing/search`,
      {
        params
      }
    );
  }

  linkSupplierMissingProduct(itemId: number, productId: number) {
    return this.http.post<NewCatalogSupplierMissingActionResult>(
      `${environment.BACKEND}/new-catalog/supplier-missing/${itemId}/link`,
      {
        product_id: productId
      }
    );
  }

  createSupplierMissingProduct(itemId: number, payload: NewCatalogSupplierMissingCreatePayload) {
    return this.http.post<NewCatalogSupplierMissingActionResult>(
      `${environment.BACKEND}/new-catalog/supplier-missing/${itemId}/create`,
      payload
    );
  }

  bulkCreateSupplierMissingProducts(payloads: NewCatalogSupplierMissingCreatePayload[]) {
    return this.http.post<NewCatalogSupplierMissingBulkCreateResult>(
      `${environment.BACKEND}/new-catalog/supplier-missing/bulk-create`,
      {
        items: payloads
      }
    );
  }

  createProductFromSupplierInfo(payload: NewCatalogCreateProductFromSupplierInfoPayload) {
    return this.http.post<NewCatalogCreateProductFromSupplierInfoResponse>(
      `${environment.BACKEND}/new-catalog/products/create-from-supplier-info`,
      payload
    );
  }

  runManualProductRepricing() {
    return this.http.post<ProductRepricingStartResult>(
      `${environment.BACKEND}/product-repricing/run`,
      {}
    );
  }
}
